import { Inject, Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { AuthService } from '../services/auth.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {

  constructor(
    private authService: AuthService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only attach the bearer token to our API calls.
    if (!request.url.includes(environment.apiURL)) {
      return next.handle(request);
    }

    // Pull the current token live from Okta's token manager (kept fresh by autoRenew)
    // rather than a value cached at login, so a long-open UI never sends an expired token.
    return from(this.getToken()).pipe(
      switchMap(token => {
        if (token) {
          request = request.clone({ setHeaders: { 'Authorization': `Bearer ${token}` } });
          if (request.url.indexOf('/cert?id') > -1 || request.urlWithParams.indexOf('/forms?form') > -1) {
            request = request.clone({ setHeaders: { 'Accept': 'application/pdf' } });
          }
        }
        return next.handle(request);
      })
    );
  }

  private async getToken(): Promise<string | null> {
    try {
      const idToken: any = await this.oktaAuth.tokenManager.get('idToken');
      if (idToken?.idToken && !this.oktaAuth.tokenManager.hasExpired(idToken)) {
        return idToken.idToken;
      }
    } catch {
      // fall through to the stored token
    }
    // Fallback to the token captured at login (the error interceptor handles a 401 if it's stale).
    return this.authService.getAuth().token || null;
  }
}
